import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { uploadImageToSupabase } from '../lib/storage';
import type { Room, Booking, Payment } from '../types';
import { format, isWithinInterval, parseISO, eachDayOfInterval, isBefore, isAfter, isSameDay, startOfDay, endOfDay } from 'date-fns';

interface RoomContextType {
  rooms: Room[];
  bookings: Booking[];
  bookingSources: string[];
  loading: boolean;
  error: string | null;
  addRoom: (room: Omit<Room, 'id'>) => Promise<void>;
  updateRoom: (id: string, room: Partial<Room>) => Promise<void>;
  deleteRoom: (id: string) => Promise<void>;
  bookRoom: (booking: Omit<Booking, 'id' | 'createdAt' | 'payments'>) => Promise<void>;
  addExternalBooking: (booking: Omit<Booking, 'id' | 'createdAt' | 'payments'>) => Promise<void>;
  updateBookingStatus: (id: string, status: 'pending' | 'confirmed' | 'cancelled') => Promise<void>;
  deleteBooking: (id: string) => Promise<void>;
  applyDiscount: (bookingId: string, discountType: 'percentage' | 'fixed', discountValue: number) => Promise<void>;
  removeDiscount: (bookingId: string) => Promise<void>;
  updateBookingNotes: (bookingId: string, adminNotes?: string, userNotes?: string) => Promise<void>;
  addPayment: (bookingId: string, payment: Omit<Payment, 'id'>) => Promise<void>;
  getRoomById: (id: string) => Room | undefined;
  getBookingById: (id: string) => Booking | undefined;
  isRoomAvailable: (roomId: string, checkIn: string, checkOut: string) => boolean;
  addBookingSource: (source: string) => Promise<void>;
  deleteBookingSource: (source: string) => Promise<void>;
  getBookedDatesForRoom: (roomId: string) => string[];
  hasDateConflict: (roomId: string, checkIn: string, checkOut: string) => boolean;
  isRoomBookable: (roomId: string) => boolean;
}

const RoomContext = createContext<RoomContextType | undefined>(undefined);

export const RoomProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [bookingSources, setBookingSources] = useState<string[]>(['Manual']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch rooms
  const fetchRooms = async () => {
    try {
      const { data, error } = await supabase
        .from('rooms')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRooms(data || []);
    } catch (err) {
      console.error('Error fetching rooms:', err);
      setError('Failed to fetch rooms');
    }
  };

  // Fetch bookings with payments
  const fetchBookings = async () => {
    try {
      const { data: bookingsData, error: bookingsError } = await supabase
        .from('bookings')
        .select('*')
        .order('created_at', { ascending: false });

      if (bookingsError) throw bookingsError;

      const { data: paymentsData, error: paymentsError } = await supabase
        .from('payments')
        .select('*');

      if (paymentsError) throw paymentsError;

      // Group payments by booking_id
      const paymentsByBooking = (paymentsData || []).reduce((acc, payment) => {
        if (!acc[payment.booking_id]) {
          acc[payment.booking_id] = [];
        }
        acc[payment.booking_id].push(payment);
        return acc;
      }, {} as Record<string, Payment[]>);

      // Combine bookings with their payments
      const bookingsWithPayments = (bookingsData || []).map(booking => ({
        ...booking,
        roomId: booking.room_id,
        userId: booking.user_id,
        userName: booking.user_name,
        userEmail: booking.user_email,
        userPhone: booking.user_phone,
        checkIn: booking.check_in,
        checkOut: booking.check_out,
        totalPrice: booking.total_price,
        originalPrice: booking.original_price || booking.total_price,
        discountType: booking.discount_type as 'percentage' | 'fixed' | 'none' || 'none',
        discountValue: booking.discount_value || 0,
        discountAmount: booking.discount_amount || 0,
        createdAt: booking.created_at,
        adminNotes: booking.admin_notes || '',
        userNotes: booking.user_notes || '',
        notesUpdatedAt: booking.notes_updated_at,
        payments: paymentsByBooking[booking.id] || []
      }));

      setBookings(bookingsWithPayments);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError('Failed to fetch bookings');
    }
  };

  // Fetch booking sources
  const fetchBookingSources = async () => {
    try {
      const { data, error } = await supabase
        .from('booking_sources')
        .select('name')
        .order('name');

      if (error) throw error;
      setBookingSources(data?.map(item => item.name) || ['Manual']);
    } catch (err) {
      console.error('Error fetching booking sources:', err);
    }
  };

  // Initial data fetch
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchRooms(),
        fetchBookings(),
        fetchBookingSources()
      ]);
      setLoading(false);
    };

    loadData();
  }, []);

  // Real-time subscriptions
  useEffect(() => {
    const roomsSubscription = supabase
      .channel('rooms_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'rooms' }, () => {
        fetchRooms();
      })
      .subscribe();

    const bookingsSubscription = supabase
      .channel('bookings_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bookings' }, () => {
        fetchBookings();
      })
      .subscribe();

    const paymentsSubscription = supabase
      .channel('payments_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'payments' }, () => {
        fetchBookings();
      })
      .subscribe();

    const sourcesSubscription = supabase
      .channel('booking_sources_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'booking_sources' }, () => {
        fetchBookingSources();
      })
      .subscribe();

    return () => {
      roomsSubscription.unsubscribe();
      bookingsSubscription.unsubscribe();
      paymentsSubscription.unsubscribe();
      sourcesSubscription.unsubscribe();
    };
  }, []);

  const addRoom = async (room: Omit<Room, 'id'>) => {
    try {
      let imageUrl = room.image_url;
      
      // If image_url is a base64 string, upload it to storage
      if (imageUrl && imageUrl.startsWith('data:image')) {
        imageUrl = await uploadImageToSupabase(imageUrl);
      }

      const { error } = await supabase.from('rooms').insert([{
        name: room.name,
        description: room.description,
        price: room.price,
        capacity: room.capacity,
        amenities: room.amenities,
        image_url: imageUrl,
        available: room.available
      }]);
      
      if (error) throw error;
    } catch (error) {
      console.error('Error adding room:', error);
      throw error;
    }
  };

  const updateRoom = async (id: string, room: Partial<Room>) => {
    try {
      let imageUrl = room.image_url;
      
      // If image_url is a base64 string, upload it to storage
      if (imageUrl && imageUrl.startsWith('data:image')) {
        imageUrl = await uploadImageToSupabase(imageUrl);
      }

      const updateData: any = { ...room };
      if (imageUrl !== undefined) {
        updateData.image_url = imageUrl;
      }

      const { error } = await supabase
        .from('rooms')
        .update(updateData)
        .eq('id', id);
      
      if (error) throw error;
    } catch (error) {
      console.error('Error updating room:', error);
      throw error;
    }
  };

  const deleteRoom = async (id: string) => {
    try {
      const { error } = await supabase
        .from('rooms')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    } catch (error) {
      console.error('Error deleting room:', error);
      throw error;
    }
  };

  // Check if a room is bookable (available and exists)
  const isRoomBookable = (roomId: string): boolean => {
    const room = rooms.find(r => r.id === roomId);
    return room ? room.available : false;
  };

  const bookRoom = async (booking: Omit<Booking, 'id' | 'createdAt' | 'payments'>) => {
    try {
      // Check if room is bookable before proceeding
      if (!isRoomBookable(booking.roomId)) {
        throw new Error('This room is currently not available for booking');
      }

      const insertData: any = {
        room_id: booking.roomId,
        user_id: booking.userId,
        user_name: booking.userName,
        user_email: booking.userEmail,
        user_phone: booking.userPhone,
        check_in: booking.checkIn,
        check_out: booking.checkOut,
        total_price: booking.totalPrice,
        status: booking.status || 'pending',
        source: booking.source || 'Manual'
      };

      // Add discount fields if they exist
      if (booking.originalPrice !== undefined) {
        insertData.original_price = booking.originalPrice;
      }
      if (booking.discountType && booking.discountType !== 'none') {
        insertData.discount_type = booking.discountType;
        insertData.discount_value = booking.discountValue || 0;
        insertData.discount_amount = booking.discountAmount || 0;
      }
      
      // Add notes if they exist
      if (booking.adminNotes) {
        insertData.admin_notes = booking.adminNotes;
      }
      if (booking.userNotes) {
        insertData.user_notes = booking.userNotes;
      }

      const { data, error } = await supabase.from('bookings').insert([insertData]).select();
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        const newBooking = {
          ...data[0],
          roomId: data[0].room_id,
          userId: data[0].user_id,
          userName: data[0].user_name,
          userEmail: data[0].user_email,
          userPhone: data[0].user_phone,
          checkIn: data[0].check_in,
          checkOut: data[0].check_out,
          totalPrice: data[0].total_price,
          createdAt: data[0].created_at,
          payments: []
        };
        setBookings(prev => [newBooking, ...prev]);
      }
    } catch (error) {
      console.error('Error booking room:', error);
      throw error;
    }
  };

  const addExternalBooking = async (booking: Omit<Booking, 'id' | 'createdAt' | 'payments'>) => {
    try {
      // Check if room is bookable before proceeding
      if (!isRoomBookable(booking.roomId)) {
        throw new Error('This room is currently not available for booking');
      }

      const { data, error } = await supabase.from('external_bookings').insert([{
        external_id: `EXT-${Date.now()}`,
        source: booking.source || 'External',
        room_id: booking.roomId,
        check_in: booking.checkIn,
        check_out: booking.checkOut,
        guest_name: booking.userName,
        guest_email: booking.userEmail,
        guest_phone: booking.userPhone,
        status: booking.status || 'pending',
        total_price: booking.totalPrice,
        admin_notes: booking.adminNotes || '',
        user_notes: booking.userNotes || ''
      }]).select();
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        const newBooking = {
          ...data[0],
          roomId: data[0].room_id,
          userId: null,
          userName: data[0].guest_name,
          userEmail: data[0].guest_email,
          userPhone: data[0].guest_phone,
          checkIn: data[0].check_in,
          checkOut: data[0].check_out,
          totalPrice: data[0].total_price,
          createdAt: data[0].created_at,
          payments: []
        };
        setBookings(prev => [newBooking, ...prev]);
      }
    } catch (error) {
      console.error('Error adding external booking:', error);
      throw error;
    }
  };

  const updateBookingStatus = async (id: string, status: 'pending' | 'confirmed' | 'cancelled') => {
    try {
      const { error } = await supabase
        .from('bookings')
        .update({ status })
        .eq('id', id);
      
      if (error) throw error;
    } catch (error) {
      console.error('Error updating booking status:', error);
      throw error;
    }
  };

  const updateBookingNotes = async (bookingId: string, adminNotes?: string, userNotes?: string) => {
    try {
      const updateData: any = {
        notes_updated_at: new Date().toISOString()
      };
      
      if (adminNotes !== undefined) {
        updateData.admin_notes = adminNotes;
      }
      if (userNotes !== undefined) {
        updateData.user_notes = userNotes;
      }

      const { error } = await supabase
        .from('bookings')
        .update(updateData)
        .eq('id', bookingId);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating booking notes:', error);
      throw error;
    }
  };

  const addPayment = async (bookingId: string, payment: Omit<Payment, 'id'>) => {
    try {
      const { error } = await supabase.from('payments').insert([{
        booking_id: bookingId,
        amount: payment.amount,
        method: payment.method,
        date: payment.date
      }]);
      
      if (error) throw error;
    } catch (error) {
      console.error('Error adding payment:', error);
      throw error;
    }
  };

  const applyDiscount = async (bookingId: string, discountType: 'percentage' | 'fixed', discountValue: number) => {
    try {
      const booking = bookings.find(b => b.id === bookingId);
      if (!booking) throw new Error('Booking not found');

      const originalPrice = booking.originalPrice || booking.totalPrice;
      let discountAmount = 0;
      let finalPrice = originalPrice;

      if (discountType === 'percentage') {
        discountAmount = (originalPrice * discountValue) / 100;
      } else {
        discountAmount = discountValue;
      }

      finalPrice = Math.max(0, originalPrice - discountAmount);

      const { error } = await supabase
        .from('bookings')
        .update({
          original_price: originalPrice,
          discount_type: discountType,
          discount_value: discountValue,
          discount_amount: discountAmount,
          total_price: finalPrice
        })
        .eq('id', bookingId);

      if (error) throw error;
    } catch (error) {
      console.error('Error applying discount:', error);
      throw error;
    }
  };

  const removeDiscount = async (bookingId: string) => {
    try {
      const booking = bookings.find(b => b.id === bookingId);
      if (!booking) throw new Error('Booking not found');

      const originalPrice = booking.originalPrice || booking.totalPrice;

      const { error } = await supabase
        .from('bookings')
        .update({
          discount_type: 'none',
          discount_value: 0,
          discount_amount: 0,
          total_price: originalPrice
        })
        .eq('id', bookingId);

      if (error) throw error;
    } catch (error) {
      console.error('Error removing discount:', error);
      throw error;
    }
  };

  const deleteBooking = async (id: string) => {
    try {
      // First delete all associated payments
      const { error: paymentsError } = await supabase
        .from('payments')
        .delete()
        .eq('booking_id', id);

      if (paymentsError) throw paymentsError;

      // Then delete the booking
      const { error: bookingError } = await supabase
        .from('bookings')
        .delete()
        .eq('id', id);

      if (bookingError) throw bookingError;
    } catch (error) {
      console.error('Error deleting booking:', error);
      throw error;
    }
  };
  const addBookingSource = async (source: string) => {
    try {
      const { error } = await supabase.from('booking_sources').insert([{ name: source }]);
      if (error) throw error;
    } catch (error) {
      console.error('Error adding booking source:', error);
      throw error;
    }
  };

  const deleteBookingSource = async (source: string) => {
    try {
      const { error } = await supabase
        .from('booking_sources')
        .delete()
        .eq('name', source);
      if (error) throw error;
    } catch (error) {
      console.error('Error deleting booking source:', error);
      throw error;
    }
  };

  const getRoomById = (id: string): Room | undefined => {
    return rooms.find(room => room.id === id);
  };

  const getBookingById = (id: string): Booking | undefined => {
    return bookings.find(booking => booking.id === id);
  };

  // Get all booked dates for a room (including partial overlaps)
  const getBookedDatesForRoom = (roomId: string): string[] => {
    const roomBookings = bookings.filter(booking => 
      booking.roomId === roomId && 
      booking.status !== 'cancelled'
    );

    const dates = roomBookings.flatMap(booking => {
      const start = parseISO(booking.checkIn);
      const end = parseISO(booking.checkOut);
      return eachDayOfInterval({ start, end }).map(date => format(date, 'yyyy-MM-dd'));
    });

    return [...new Set(dates)]; // Remove duplicates
  };

  // Check if there's a date conflict for booking (12 PM to 12 PM system)
  const hasDateConflict = (roomId: string, checkIn: string, checkOut: string): boolean => {
    const roomBookings = bookings.filter(booking => 
      booking.roomId === roomId && 
      booking.status !== 'cancelled'
    );

    const requestedStart = parseISO(checkIn);
    const requestedEnd = parseISO(checkOut);

    return roomBookings.some(booking => {
      const bookingStart = parseISO(booking.checkIn);
      const bookingEnd = parseISO(booking.checkOut);

      // For 12 PM to 12 PM system, we need to check if any day overlaps
      // A booking from July 2 to July 3 means:
      // - Check-in: July 2 at 12 PM
      // - Check-out: July 3 at 12 PM
      // So July 2 night is occupied, but July 3 night is available

      // Check for any overlap in the date ranges
      const hasOverlap = (
        // Requested start is before booking end AND requested end is after booking start
        (requestedStart < bookingEnd && requestedEnd > bookingStart) ||
        
        // Same start date (both want to check in on same day)
        isSameDay(requestedStart, bookingStart) ||
        
        // Same end date (both want to check out on same day)
        isSameDay(requestedEnd, bookingEnd) ||
        
        // Requested check-in on existing check-out day (not allowed in 12PM system)
        isSameDay(requestedStart, bookingEnd) ||
        
        // Requested check-out on existing check-in day (not allowed in 12PM system)
        isSameDay(requestedEnd, bookingStart)
      );

      return hasOverlap;
    });
  };

  // Enhanced room availability check for 12 PM to 12 PM system
  const isRoomAvailable = (roomId: string, checkIn: string, checkOut: string): boolean => {
    // First check if room is bookable (available in admin settings)
    if (!isRoomBookable(roomId)) {
      return false;
    }
    
    // Then check for date conflicts
    return !hasDateConflict(roomId, checkIn, checkOut);
  };

  return (
    <RoomContext.Provider
      value={{
        rooms,
        bookings,
        bookingSources,
        loading,
        error,
        addRoom,
        updateRoom,
        deleteRoom,
        bookRoom,
        addExternalBooking,
        updateBookingStatus,
        applyDiscount,
        removeDiscount,
        deleteBooking,
        updateBookingNotes,
        addPayment,
        getRoomById,
        getBookingById,
        isRoomAvailable,
        addBookingSource,
        deleteBookingSource,
        getBookedDatesForRoom,
        hasDateConflict,
        isRoomBookable,
      }}
    >
      {children}
    </RoomContext.Provider>
  );
};

export const useRooms = () => {
  const context = useContext(RoomContext);
  if (context === undefined) {
    throw new Error('useRooms must be used within a RoomProvider');
  }
  return context;
};